import ICanvasInputListener from "../logic/ICanvasInputListener";
import GeometryCommand from "./GeometryCommand";
import CommandIdEnum from "../enums/CommandIdEnums";
import CanvasInputListenerModeEnum from "../enums/CanvasInputListenerModeEnum";
import RaycastIntersection from "../logic/RaycastIntersection";
import Document from "../model/document/Document";
import ISceneObject from "../model/sceneObject/ISceneObject";
import i18n from "../localization/i18n";
import { SceneObjectStyle, SceneObjectStyleEnum, getSceneObjectStyle } from "../model/sceneObject/SceneObjectStyle";
import CommandStack from "../logic/CommandStack";
import UserRightScopeEnum from "../enums/UserRightScopeEnum";
import { useUserStateStore } from "../store/userStateStore";
import SceneObjectData from "../model/sceneObjectData/SceneObjectData";

class RemoveFromDocumentCmd extends GeometryCommand {

    removeIndicatorObj: ISceneObject | undefined;
    currentUserId: string;
    
    // Custom style to indicate removal
    #toBeRemovedStyle = getSceneObjectStyle(SceneObjectStyleEnum.Hovered);
    #noPermissionToRemoveStyle = getSceneObjectStyle(SceneObjectStyleEnum.Disabled);

    constructor(    commandStack: CommandStack,
                    canvasInputListener: ICanvasInputListener, 
                    document: Document) {
        super(CommandIdEnum.RemoveFromDocument, commandStack,  canvasInputListener, document ); // Call parent constructor
        this.commandNameResourceKey = 'command.removeFromDocument.title';
        this.initialPrompResourceKey = 'command.removeFromDocument.selectObjectToRemove';

        const userId = useUserStateStore.getState().currentUserId;
        if (userId == null) {
            this.currentUserId = "";
            console.error("Cannot continue without a user ID");
            return;
        }
        this.currentUserId = userId;
    }

    start() {
        super.start(); // Optionally call parent method
        this.addToStack();
        this.setDrawableStyle(this.#toBeRemovedStyle);
        this.connectMouse();

        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS);

        const sceneObjectGroups = this.getDocument().sceneObjects.map(so => so.geometry.group);
        this.canvasInputListener.setTargetObjects(sceneObjectGroups);
    }

    canDelete(sceneObjectData: SceneObjectData): boolean {
        const userRight = this.getDocument().userRight;
        
        if (userRight.delete === UserRightScopeEnum.All) {
                return true;
        } 
        else if (userRight.delete === UserRightScopeEnum.Own) {
                // Check if the object is owned by the current user
                // If ownedBy is undefined, we assume it's created by someone else or system, so no delete? 
                if (sceneObjectData.ownedBy && sceneObjectData.ownedBy === this.currentUserId) {
                    return true;
                }
            }
        
        return false;
    }

    point(raycastResult: RaycastIntersection): boolean {

        if (!raycastResult?.object) {
            console.warn("No object found under the pointer.");
            return false;
        }

        const sceneObj = this.getSceneObjectByThreeJsObject(raycastResult.object);
        if (sceneObj) {

            // Check if user has rights to delete this object
            if (!this.canDelete(sceneObj.data)) {
                this.showInfoMessage('command.removeFromDocument.noPermission'); // You may need to add this localization key or use a generic one
                return false;
            }

            this.removeEntity(sceneObj.data.uuid).then( removed => {
                if (removed) {
                    this.showInfoMessage('command.removeFromDocument.objectRemoved');
                    this.getDocument().removeFromDocument(sceneObj);
                    this.showInfoMessage('command.removeFromDocument.selectObjectToRemove');
                    this.hideIndicator();
                }
                else {
                    console.error("Failed to remove object from document.");
                }
            });
        }
    
        return true;
    }

    end() {
        this.canvasInputListener.resetTargetObjects();
        super.end(); // Optionally call parent method
        super.removeFromStack();
        if (this.removeIndicatorObj)
            this.removeIndicatorObj.dispose();
    }

    showIndicator(sceneObject: ISceneObject) {
        this.removeIndicatorObj = sceneObject.clone(true); 
        
        this.addToDrawables(this.removeIndicatorObj);
    }

    hideIndicator() {
        if (this.removeIndicatorObj) {
            this.removeAllDrawables();
            this.removeIndicatorObj.dispose();
            this.removeIndicatorObj = undefined;
        }
    }

    pointerMove(raycastResult: RaycastIntersection | undefined): boolean {
        
        // If there is no raycast result, hide the indicator
        if (!raycastResult?.object)
        {
            this.hideIndicator();
            //console.log("No raycast result");
            this.showInfoMessage('command.removeFromDocument.selectObjectToRemove');
            return true;
        }

        const sceneObj = this.getSceneObjectByThreeJsObject(raycastResult.object);
        
        if (sceneObj) {

            // If we are already showing the correct indicator, do nothing
            if (sceneObj.data.uuid  === this.removeIndicatorObj?.data.uuid) {
                return true; // Already showing the correct indicator
            }
            
            if (this.canDelete(sceneObj.data)) {
                this.setDrawableStyle(this.#toBeRemovedStyle);
                this.showInfoMessage('command.removeFromDocument.clickToRemove'); 
            }
            else {
                this.setDrawableStyle(this.#noPermissionToRemoveStyle);
                this.showInfoMessage('command.removeFromDocument.noPermission'); 
            }

            this.showIndicator(sceneObj);
        }

        return true;
    }
    
}

export default RemoveFromDocumentCmd;